<script setup lang="ts">
    import TableLoadingOverlay from '@/js/components/dataTable/TableLoadingOverlay.vue';
    import TablePaginator from '@/js/components/dataTable/TablePaginator.vue';
    import { __ } from '@/js/stores/expressions';
    import { Column, DataTable, DataTableColumnReorderEvent, PageState } from 'primevue';
    import { ref, watch, nextTick, computed } from 'vue';
    import FirstColumnHeader from '@/js/components/dataTable/FirstColumnHeader.vue';
    import FirstColumnBody from '@/js/components/dataTable/FirstColumnBody.vue';
    import TableToolbar from '@/js/components/dataTable/TableToolbar.vue';
    import { ResponseColumnHeader } from '@/js/services/api/interaction/types';
    import ColumnBody from '@/js/components/dataTable/ColumnBody.vue';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import {
        useNumericQueryParam,
        useQueryParam,
        useQueryString,
    } from '@/js/composables/useQueryString';
    import { useTablePageSize } from '@/js/composables/useTablePageSize';
    import {
        InteractionTableStoreName,
        useInteractionTableStoreByName,
    } from '@/js/stores/interactions-table';
    import ColumnHeader from '@/js/components/dataTable/ColumnHeader.vue';
    import ProtocolTableExpandedRow from './ProtocolTableExpandedRow.vue';
    import {
        useGetProtocolTable,
        useMutationDeleteProtocols,
        useMutationUpdateProtocolApprovalStatus,
        useMutationUpdateProtocolTableLayout,
    } from '@/js/services/api/protocol/protocolQueries';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { useSyncedProtocols } from '@/js/stores/synced-protocols';
    import { useSelfCoachedProtocols } from '@/js/stores/self-coached-protocols';
    import { storeToRefs } from 'pinia';
    import TableFABs from '@/js/components/dataTable/TableFABs.vue';
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import { usePermissions } from '@/js/composables/usePermissions';
    import { PermissionArea, QualityAssuranceModulePermissionFlag } from '@/js/consts/permissions';
    import ToggleColumnModal from '@/js/components/dataTable/ToggleColumnModal.vue';
    import FrozenColumnFade from '@/js/components/dataTable/FrozenColumnFade.vue';
    import BottomScrollFade from '@/js/components/dataTable/BottomScrollFade.vue';
    import { CheckIcon, TrashIcon } from '@lucide/vue';
    import { QK } from '@/js/consts/query-keys.ts';
    import { useQuery } from '@tanstack/vue-query';
    import { getProtocolCount } from '@/js/services/api/protocol/protocolApi.ts';

    const { hasPermission } = usePermissions();

    const canApproveProtocols = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.ApproveProtocols
    );

    const canAccessQA = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.Access
    );

    const canCreateForm = hasPermission(
        PermissionArea.QualityAssuranceModule,
        QualityAssuranceModulePermissionFlag.CreateForm
    );

    const filterStore = useFilterStoreByName(FilterStoreName.SharedFilter);

    const { requestData } = storeToRefs(filterStore);

    const delayedRequestDataTimeout = ref<number | undefined>();
    const delayedRequestData = ref(requestData.value);
    const delayedLoading = ref(false);

    watch(
        requestData,
        (newRequestData, oldRequestData) => {
            if (
                oldRequestData &&
                JSON.stringify(newRequestData) === JSON.stringify(oldRequestData)
            ) {
                return;
            }

            clearTimeout(delayedRequestDataTimeout.value);

            delayedLoading.value = true;

            delayedRequestDataTimeout.value = window.setTimeout(() => {
                delayedRequestData.value = newRequestData;
                delayedLoading.value = false;
            }, 1000);
        },
        { immediate: true }
    );

    const interactionsTableStore = useInteractionTableStoreByName(
        InteractionTableStoreName.ProtocolTable
    );

    const querystring = useQueryString();

    const page = useNumericQueryParam('page', {
        startIndex: 0,
        oneBased: true,
        updateMode: 'push',
    });
    const pageSize = useTablePageSize('protocol');
    const { value: sortFieldId } = useQueryParam('field', {
        defaultValue: '',
        updateMode: 'replace',
    });
    const { value: sortOrder } = useQueryParam('order', {
        defaultValue: '',
        updateMode: 'replace',
    });

    const { data: protocols, isFetching } = useGetProtocolTable(
        page,
        pageSize,
        sortFieldId,
        sortOrder,
        delayedRequestData
    );

    // Nested under the table key so every protocol mutation that invalidates the table also refreshes the count.
    const countQueryKey = computed(() => [
        QK.PROTOCOL.TABLE,
        QK.PROTOCOL.COUNT,
        delayedRequestData.value,
    ]);

    const count = useQuery({
        queryKey: countQueryKey,
        queryFn: () => getProtocolCount(delayedRequestData.value),
        placeholderData: 0,
    });

    const updateTableLayoutMutation = useMutationUpdateProtocolTableLayout();

    const deleteProtocols = useMutationDeleteProtocols();

    const updateApprovalStatus = useMutationUpdateProtocolApprovalStatus();
    const syncedProtocols = useSyncedProtocols();
    const selfCoachedProtocols = useSelfCoachedProtocols();

    watch(
        protocols,
        (newData) => {
            newData?.rowCells
                .filter((row) => row.isSyncedFromV1)
                .forEach((row) => syncedProtocols.add(row.id));
            newData?.rowCells
                .filter((row) => row.isSelfCoached)
                .forEach((row) => selfCoachedProtocols.add(row.id));
        },
        { immediate: true }
    );

    watch(protocols, (newData) => {
        if (newData) {
            const rowIdList = newData.rowCells.map((row) => row.id);
            interactionsTableStore.setRowsIdList(rowIdList);
            interactionsTableStore.setExpandableColumns(
                newData.columnHeaders as ResponseColumnHeader[]
            );
        }
    });

    const tableKey = ref(0);

    const onColumnReorder = (event: DataTableColumnReorderEvent) => {
        const scrollLeft =
            (tableRef.value?.querySelector('.p-datatable-table-container') as HTMLElement)
                ?.scrollLeft ?? 0;
        interactionsTableStore.onReorderTableColumn(event);
        interactionsTableStore.ensureProtocolColumnsFirst();
        tableKey.value++;
        nextTick(() => {
            const newWrapper = tableRef.value?.querySelector(
                '.p-datatable-table-container'
            ) as HTMLElement | null;
            if (newWrapper) newWrapper.scrollLeft = scrollLeft;
        });
        persistTableLayout();
    };

    const paginationChange = async (e: PageState) => {
        const newPage = e.rows == pageSize.value ? e.page + 1 : 1;

        await querystring.setMany({ page: newPage, rows: e.rows }, 'push');
    };

    const onCustomSort = async (fieldId: string, order: 'asc' | 'desc') => {
        await querystring.setMany({ field: fieldId, order: order }, 'replace');
    };

    const selectedProtocolIds = computed(() =>
        Object.keys(interactionsTableStore.selectedRows).filter(
            (id) => interactionsTableStore.selectedRows[id]
        )
    );

    const selectionIncludesSyncedProtocol = computed(() =>
        selectedProtocolIds.value.some((id) => syncedProtocols.has(id))
    );

    const canApproveSelection = computed(
        () =>
            canApproveProtocols.value ||
            (selectedProtocolIds.value.length > 0 &&
                selectedProtocolIds.value.every((id) => selfCoachedProtocols.has(id)))
    );

    const deleteDisabledReason = computed(() => {
        if (selectionIncludesSyncedProtocol.value) return __('protocol-synced-from-v1-tooltip');
        if (!canCreateForm.value) return __('delete-protocol-requires-create-form-permission');
        return null;
    });

    const onDeleteProtocols = async () => {
        const protocolIds = selectedProtocolIds.value;

        if (protocolIds.length === 0) return;

        await deleteProtocols.mutateAsync({ protocolIds });
    };

    const onApproveProtocols = async () => {
        const protocolIds = selectedProtocolIds.value;

        if (protocolIds.length === 0) return;

        await updateApprovalStatus.mutateAsync({
            protocolIds,
        });
    };

    const tableRef = ref<HTMLElement | null>(null);

    const getRowValue = (column: ResponseColumnHeader) => {
        if (column.type === 'ProtocolResults') {
            return 'ProtocolResults';
        }

        return column.translationKey.split('table-protocol-column-')[1];
    };

    const shouldHide = (col: ResponseColumnHeader) => {
        if (col.expandsFromParentId) {
            return interactionsTableStore.expandedColumns[col.expandsFromParentId]?.open === false;
        }
        return col.order == null || col.order === undefined;
    };
    const showColumnModal = ref(false);

    const persistTableLayout = () => {
        let topLevelIndex = 0;
        // TODO: replace with sequential ordering when BE removes ghost column from layout DB
        const request = interactionsTableStore.tableColumns
            ?.filter((col) => !col.expandsFromParentId && col.type !== 'ProtocolResults')
            .map((col) => ({
                id: col.id,
                order: col.order != null ? 10000 + topLevelIndex++ : null,
            }));
        if (request) updateTableLayoutMutation.mutate(request);
    };

    const totalRows = computed(() => {
        if (!count.isFetching.value && count.data.value !== undefined) {
            return count.data.value;
        }
        return protocols.value?.pagination.totalRows || 0;
    });
</script>
<template>
    <div
        ref="tableRef"
        class="echo-table-wrapper echo-table-wrapper--fill echo-table-wrapper--clickable-rows"
    >
        <TableToolbar
            :table-store-name="InteractionTableStoreName.ProtocolTable"
            :container-ref="tableRef"
            @show-column-modal="showColumnModal = true"
        />
        <DataTable
            :key="tableKey"
            v-model:expanded-rows="interactionsTableStore.expandedRows"
            data-key="id"
            lazy
            paginator
            scrollable
            scroll-height="flex"
            :reorderable-columns="true"
            :value="protocols?.rowCells || []"
            :loading="isFetching || delayedLoading"
            :total-records="totalRows"
            :rows="pageSize"
            :page="page"
            :first="page * pageSize + 1"
            column-resize-mode="expand"
            :resizable-columns="false"
            @column-reorder="onColumnReorder"
            @row-click="(e) => interactionsTableStore.toggleRowExpansion(e.data.id)"
        >
            <template #empty>
                <div class="h-100 flex items-center justify-center">
                    <h2 v-if="!isFetching" class="text-center">
                        {{ __('protocol-table-empty-message') }}
                    </h2>
                </div>
            </template>
            <template #loadingicon>
                <TableLoadingOverlay />
            </template>
            <Column
                field="id"
                column-key="id"
                :row-reorder="false"
                :reorderable-column="false"
                :frozen="true"
                class="w-79.25 min-w-79.25 max-w-79.25"
            >
                <template #header>
                    <FirstColumnHeader
                        :total-records="totalRows"
                        :label="__('protocol-table-total-protocols')"
                    />
                </template>
                <template #body="{ data }">
                    <FirstColumnBody
                        :row-id="data.id"
                        :is-new="data.isNew && !interactionsTableStore.openedRows[data.id]"
                        :checked="!!interactionsTableStore.selectedRows[data.id]"
                        :channel-icon="data.channelIcon"
                        :title="data.formTitle"
                        :subtitle="__('protocol-number-label', { number: data.protocolNumber })"
                        :table-store-name="InteractionTableStoreName.ProtocolTable"
                        :gray-scale="data.isApproved === false"
                    />
                </template>
            </Column>
            <Column
                v-for="col in interactionsTableStore.tableColumns"
                :key="col.id"
                :field="col.id"
                :column-key="col.id"
                :hidden="shouldHide(col)"
                :reorderable-column="
                    col.type !== 'ProtocolResults' &&
                    (!col.expandsFromParentId || !!interactionsTableStore.expandedColumns[col.id])
                "
            >
                <template #header>
                    <ColumnHeader
                        :sort-order="sortOrder as any"
                        :sort-field-id="sortFieldId || undefined"
                        :header="col"
                        :filter-store-name="FilterStoreName.SharedFilter"
                        :expandable="!!interactionsTableStore.expandedColumns[col.id]"
                        :expanded="
                            interactionsTableStore.expandedColumns[col.id]?.open || undefined
                        "
                        :color="col.color"
                        @sort="onCustomSort"
                        :disable-layout-shift="
                            col.type === 'ProtocolResults' || !!col.expandsFromParentId
                        "
                        @toggle-expansion="interactionsTableStore.toggleColumnExpansion($event)"
                        @hide-column="persistTableLayout"
                    />
                </template>
                <template #body="{ data }">
                    <ColumnBody
                        :field="getRowValue(col)"
                        :data="data[col.id]"
                        :show-as-percentage="!interactionsTableStore.showCoachResultAsPoints"
                        :edited-by-human="data.editedByHuman"
                    />
                </template>
            </Column>
            <template #expansion="{ data }">
                <ProtocolTableExpandedRow :id="data.id" :container-ref="tableRef" />
            </template>
            <template #paginatorcontainer="slotProps">
                <TablePaginator
                    :first="slotProps.first"
                    :rows="slotProps.rows"
                    :page="slotProps.page"
                    :total-records="slotProps.totalRecords ?? 0"
                    :page-count="slotProps.pageCount ?? 0"
                    @pagination-change="paginationChange"
                />
            </template>
        </DataTable>
        <FrozenColumnFade :container-ref="tableRef" />
        <BottomScrollFade :container-ref="tableRef" />
    </div>
    <TableFABs v-if="interactionsTableStore.selectionMode">
        <EchoTooltip
            v-if="canApproveSelection"
            :label="__('protocol-synced-from-v1-tooltip')"
            :disabled="!selectionIncludesSyncedProtocol"
            :tabindex="selectionIncludesSyncedProtocol ? 0 : undefined"
        >
            <EchoButton
                variant="primary"
                :disabled="selectionIncludesSyncedProtocol"
                @click="onApproveProtocols"
            >
                {{ __('protocols-approve-all') }}
                <template #icon-right><CheckIcon /></template>
            </EchoButton>
        </EchoTooltip>
        <EchoTooltip
            v-if="canAccessQA"
            :label="deleteDisabledReason ?? ''"
            :disabled="!deleteDisabledReason"
            :tabindex="deleteDisabledReason ? 0 : undefined"
        >
            <EchoButton
                variant="secondary"
                :disabled="!!deleteDisabledReason"
                @click="onDeleteProtocols"
            >
                {{ __('protocols-delete-all') }}
                <template #icon-right><TrashIcon /></template>
            </EchoButton>
        </EchoTooltip>
    </TableFABs>
    <ToggleColumnModal
        :store-name="InteractionTableStoreName.ProtocolTable"
        :show-column-modal="showColumnModal"
        @close="showColumnModal = false"
        @submit="persistTableLayout"
    />
</template>
